import type { ComponentProps } from 'react';
import React, { useCallback } from 'react';

import { Button } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

import { usePsfPromoteOrSharePageData } from '#src/app/pages/psf/promoteOrShare/pageContext';
import { PetitionShareSlideUp } from '#src/app/pages/psf/promoteOrShare/shared/components';
import { useShareActions } from '#src/app/pages/psf/promoteOrShare/shared/hooks';
import { ShareEducationModalProvider, useShareEducationModal } from '#src/app/shared/hooks/share';
import { useTrackExperimentStarted } from '#src/app/shared/hooks/tracking';

/**
 * Module-level dedup so all ShareSlideUp instances on the same petition share a single
 * "fired" state. Survives across mounts within the same SPA session; resets on full reload.
 */
const firedExperiments = new Set<string>();

/** Exposed for tests to reset the module-level dedup between cases. */
export const resetFiredExperimentsForTesting = (): void => {
	firedExperiments.clear();
};

type ButtonVariant = ComponentProps<typeof Button>['variant'];

type ShareSlideUpProps = {
	shareButtonLabel: string;
	buttonVariant?: ButtonVariant;
};

export const ShareSlideUp: React.FC<ShareSlideUpProps> = ({ shareButtonLabel, buttonVariant = 'secondary' }) => {
	const {
		data: { buttonConfigs, continueButtonProps, shareVisualUrl },
		actions: { handleOpenShareSlideUp, handleCloseShareSlideUp },
	} = useShareActions();

	const {
		data: { petition, psfComboRemoveAutoProgressionExperiment },
	} = usePsfPromoteOrSharePageData();

	const { variation, experimentName, treated } = psfComboRemoveAutoProgressionExperiment;
	const closeOnSocialShare = variation !== 'variant_2' && variation !== 'variant_3';

	// `resetKeys: [petition.id]` covers SPA navigation between petitions: the route reuses this
	// component instance across slug changes (no remount), so without this the per-mount dedup
	// would silently drop the second petition's event since the variation is deterministic per user.
	const trackExperimentStarted = useTrackExperimentStarted(psfComboRemoveAutoProgressionExperiment, {
		resetKeys: [petition.id],
	});

	const handleOpen = useCallback(() => {
		handleOpenShareSlideUp();

		if (!treated) return;

		const dedupKey = `${experimentName}:${petition.id}`;
		if (firedExperiments.has(dedupKey)) return;
		firedExperiments.add(dedupKey);

		void trackExperimentStarted();
	}, [handleOpenShareSlideUp, treated, experimentName, petition.id, trackExperimentStarted]);

	/**
	 * Single modal instance shared across all channel share actions (instagram,
	 * instagramStories, tiktok).  Channel hooks read openModal from context and
	 * swap in their own content at open-time.
	 */
	const [openEducationModal, educationModal] = useShareEducationModal();

	return (
		<ShareEducationModalProvider value={openEducationModal}>
			<PetitionShareSlideUp
				petition={{
					ask: petition.ask,
					description: petition.description,
					photo: petition.photo?.petitionSmall.url,
				}}
				buttonConfigs={buttonConfigs}
				shareVisual={shareVisualUrl}
				closeOnSocialShare={closeOnSocialShare}
				trigger={
					<div className="w-full md:hidden">
						<Button
							className={cn('mt-2 w-full [&>div]:leading-5', { 'py-3': buttonVariant === 'tertiary' })}
							variant={buttonVariant}
							data-qa="share-button-slide-up"
							data-testid="share-button-slide-up"
							onClick={handleOpen}
						>
							{shareButtonLabel}
						</Button>
					</div>
				}
				onClose={handleCloseShareSlideUp}
				continueButtonProps={continueButtonProps}
			/>
			{educationModal}
		</ShareEducationModalProvider>
	);
};
